<template>
  <section v-if="activeSubsection" class="row">
    <card>
      <language-selector />
      <div class="d-flex flex-wrap">
        <div class="col-12 mb-4">
          <div class="row">
            <div class="col-12">
              <h3>Editing {{ activeSubsection.label }}</h3>
              <button class="btn btn-sm btn-primary" @click="viewAll">
                View all releases
              </button>
              <button
                class="btn btn-sm btn-primary ml-1"
                @click="saveSubsection(activeSubsection)"
              >
                Save
              </button>
            </div>
          </div>
        </div>
        <div class="col-6 mb-4">
          <div class="row">
            <div class="col-6 mb-4">
              Label
              <input
                type="text"
                class="form-control form-control-lg"
                v-model="activeSubsection.label"
                placeholder="i.e. apartment"
              />
            </div>
            <div class="col-6 mb-4">
              Display Name
              <localised-field-indicator />
              <input
                :dir="activeLanguage.rtl ? 'rtl' : 'ltr'"
                type="text"
                class="form-control form-control-lg"
                v-model="activeSubsection.displayName[activeLanguage.code]"
                placeholder="i.e. apartment"
              />
            </div>
            <div class="col-6 mb-4">
              Access PIN
              <input
                type="text"
                inputmode="numeric"
                class="form-control form-control-lg"
                v-model.trim="activeSubsection.accessCode"
                placeholder="Optional"
              />
            </div>

            <div class="col-12 mb-4">
              Description
              <localised-field-indicator />
              <ck-editor
                id="subsectionDescription"
                v-model="activeSubsection.description[activeLanguage.code]"
                :dir="activeLanguage.rtl ? 'rtl' : 'ltr'"
                :key="activeLanguage.code"
                :language="activeLanguage.code"
              ></ck-editor>
            </div>

            <div class="col-12 mb-4">
              Issuu Embed Url
              <localised-field-indicator />
              <input
                type="text"
                class="form-control form-control-lg"
                v-model="activeSubsection.issuuEmbedUrl[activeLanguage.code]"
                placeholder=""
              />
            </div>

            <div class="col-12 mb-4">
              iFrames
              <localised-field-indicator />
              <input-key-value-pairs
                class="form-control form-control-lg"
                key-label="label"
                value-label="url"
                v-model="activeSubsection.iframes[activeLanguage.code]"
              ></input-key-value-pairs>
            </div>

            <div class="col-12 mb-4">
              Subsection Background
              <localised-field-indicator />
              <input-asset
                v-model="activeSubsection.backgroundImage[activeLanguage.code]"
              ></input-asset>
            </div>
            <div class="col-12 mb-4">
              <fieldset class="form-group">
                <label>Video Settings</label>
                <div class="d-flex">
                  <input
                    type="checkbox"
                    v-model="activeSubsection.backgroundVideoSettings.loop"
                  />
                  <span class="ml-2">Loop Video</span>
                </div>
                <div class="d-flex">
                  <input
                    type="checkbox"
                    v-model="
                      activeSubsection.backgroundVideoSettings.showControls
                    "
                  />
                  <span class="ml-2"
                    >Show Controls<InfoBox
                      >Show the video player controls (pause, mute).</InfoBox
                    ></span
                  >
                </div>
                <div class="d-flex">
                  <input
                    type="checkbox"
                    v-model="activeSubsection.backgroundVideoSettings.delayTags"
                  />
                  <span class="ml-2"
                    >Delay Tags
                    <InfoBox
                      >Delay animating tags in until after video play back has
                      finished.</InfoBox
                    ></span
                  >
                </div>
                <div class="d-flex">
                  <input
                    type="checkbox"
                    v-model="
                      activeSubsection.backgroundVideoSettings.startMuted
                    "
                  />
                  <span class="ml-2"
                    >Start Muted
                    <InfoBox
                      >Start the video with the sound muted. Can be unmuted if
                      "Show Controls" is checked.</InfoBox
                    ></span
                  >
                </div>
              </fieldset>
            </div>

            <div class="col-12 mb-4">
              Card Background Image
              <input-asset
                v-model="activeSubsection.cardBackgroundImage"
              ></input-asset>
            </div>

            <div class="col-12 mb-4">
              Subsection Minimap
              <input-asset
                v-model="activeSubsection.minimapImage"
              ></input-asset>
            </div>

            <div class="col-12 mb-4 subsection-shortcuts">
              Subsection Shortcuts
              <div>
                <input
                  class="form-control my-2"
                  type="text"
                  placeholder="Filter"
                  v-model="subsectionShortcutSelectionsFilter"
                />
              </div>
              <div class="subsection-shortcuts__inner">
                <div
                  v-for="(option, i) in subsectionShortcutOptions"
                  :key="option.value"
                  class="shortcut-wrapper"
                  :class="{ 'shortcut-wrapper--darken': i % 2 == 0 }"
                >
                  <label :for="option.value">
                    <input
                      type="checkbox"
                      :name="option.value"
                      :id="option.value"
                      :value="option.value"
                      v-model="subsectionShortcutSelections"
                    />
                    {{ option.label }}
                  </label>
                </div>
              </div>

              <div
                v-if="activeSubsection.subsectionShortcuts.length"
                class="mt-4"
              >
                Active shortcuts
                <div class="">
                  <Draggable v-model="activeSubsection.subsectionShortcuts">
                    <div
                      v-for="(
                        option, i
                      ) in activeSubsection.subsectionShortcuts"
                      :key="option.value"
                      class="shortcut-wrapper shortcut-wrapper--draggable"
                    >
                      <i class="ti-line-double"></i>
                      <label>
                        {{ getShortcutLabelById(option.subsectionId) }}
                      </label>
                      <div class="shortcut-input-group">
                        <label for="">
                          Shortcut Label
                          <localised-field-indicator />
                        </label>
                        <input
                          class="form-control form-control--sm"
                          type="text"
                          v-model="option.label[activeLanguage.code]"
                        />
                      </div>
                    </div>
                  </Draggable>
                </div>
              </div>
            </div>

            <div class="col-12 mb-2">
              <hr />
            </div>

            <div class="col-12 mb-4">Visibility Settings</div>

            <div class="col-6 col-lg-4 mb-4">
              <div class="d-flex align-items-center flex-column">
                <toggle-button
                  v-model="activeSubsection.showOnMap"
                  color="#28a745"
                  :sync="true"
                  :labels="false"
                  :width="50"
                  :height="30"
                  style="margin: 3px 0 0"
                />
                <span class="text-center">Show Tag and Polygon</span>
              </div>
            </div>

            <div class="col-6 col-lg-4 mb-4">
              <div class="d-flex align-items-center flex-column">
                <toggle-button
                  v-model="activeSubsection.showNavigationSlide"
                  color="#28a745"
                  :sync="true"
                  :labels="false"
                  :width="50"
                  :height="30"
                  style="margin: 3px 0 0"
                />
                <span class="text-center">Show Navigation Slide</span>
              </div>
            </div>

            <div class="col-6 col-lg-4 mb-4">
              <div class="d-flex align-items-center flex-column">
                <toggle-button
                  v-model="activeSubsection.showOnWebfeed"
                  color="#28a745"
                  :sync="true"
                  :labels="false"
                  :width="50"
                  :height="30"
                  style="margin: 3px 0 0"
                />
                <span class="text-center">Show on Webfeed</span>
              </div>
            </div>

            <div class="col-12 mb-2">
              <hr />
            </div>

            <div class="col-12 mb-4">Misc Settings</div>

            <div class="col-6 col-lg-4 mb-4">
              <div class="d-flex align-items-center flex-column">
                <toggle-button
                  v-model="activeSubsection.isDisplayVillage"
                  color="#28a745"
                  :sync="true"
                  :labels="false"
                  :width="50"
                  :height="30"
                  style="margin: 3px 0 0"
                />
                <span class="text-center">Is Display Village</span>
              </div>
            </div>
            <div class="col-6 col-lg-4 mb-4">
              <div class="d-flex align-items-center flex-column">
                <toggle-button
                  v-model="activeSubsection.isFilterHidden"
                  color="#28a745"
                  :sync="true"
                  :labels="false"
                  :width="50"
                  :height="30"
                  style="margin: 3px 0 0"
                />
                <span class="text-center">Is Filter Slideout Hidden</span>
              </div>
            </div>
          </div>
        </div>

        <div class="col-6 mb-4">
          <div class="row">
            <div class="col-12 mb-4">
              Associated Properties
              <input-multi-select
                v-model="activeSubsection.propertyIds"
                :options="propertyOptions"
              ></input-multi-select>
            </div>

            <div class="col-12 mb-4">
              Associated Subsections*
              <p>
                <small
                  >*Only populate for subsections that require subsections as
                  children. i.e Building > Floor</small
                >
              </p>
              <input-multi-select
                v-model="activeSubsection.subsectionIds"
                :options="subsectionOptions"
              ></input-multi-select>
            </div>

            <div class="col-12 mb-4">
              Subsection Type*
              <p>
                <small
                  >*Only populate for subsections that require subsections as
                  children. i.e Building > Floor</small
                >
              </p>
              <input
                type="text"
                class="form-control form-control-lg"
                v-model="activeSubsection.subsectionType"
                placeholder="i.e. block, floor, etc."
              />
            </div>

            <div class="col-12 mb-4">
              <fieldset class="form-group">
                <InputTransitionVideos
                  v-model="activeSubsection.pageTransitionVideos"
                />
              </fieldset>
            </div>
          </div>
        </div>

        <div class="col-12 mb-4 card-update-tags">
          <div class="title-row">
            <h4 class="card-title mb-0">Edit Canvas</h4>

            <div class="info-icon-wrapper">
              <div class="edit-tags-info">
                <InfoBox>
                  <div class="edit-tags-info__section">
                    <div class="edit-tags-info__label">Basics</div>
                    <ul>
                      <li>
                        Click <strong>Add Tag</strong> to create a new tag.
                      </li>
                      <li>Drag tags to reposition them on the map.</li>
                      <li>
                        Click a tag to select it;
                        <strong>double-click</strong> to open the Edit Menu (pen
                        icon) or click the icon itself.
                      </li>
                    </ul>
                  </div>

                  <div class="edit-tags-info__section">
                    <div class="edit-tags-info__label">Toolbar</div>
                    <ul>
                      <li>
                        Use the left toolbar for <strong>Zoom</strong>,
                        <strong>Undo / Redo</strong> and
                        <strong>Alignment</strong> tools.
                      </li>
                      <li>Selected tags can be aligned or moved together.</li>
                    </ul>
                  </div>

                  <div class="edit-tags-info__section">
                    <div class="edit-tags-info__label">Shortcuts</div>
                    <ul>
                      <li>Hold <strong>Shift</strong> to multi-select tags.</li>
                      <li>
                        Hold <strong>Shift</strong> when editing polygons to
                        toggle '+' icon used for adding vertices.
                      </li>
                      <li>
                        <strong>Double-Click</strong> a vertex/point on a
                        polygon to remove it.
                      </li>
                      <li>
                        <strong>Shift + Double-Click</strong> a tag to select
                        all tags of that type.
                      </li>
                      <li>
                        Use <strong>Shift +</strong> to zoom in and
                        <strong>Shift -</strong> to zoom out.
                      </li>
                      <li>
                        Press <strong>Delete</strong> or
                        <strong>Backspace</strong> to remove a selected
                        tag/element.
                      </li>
                      <li>
                        Press <strong>Esc</strong> to clear selection or exit
                        edit mode.
                      </li>
                      <li>
                        Use <strong>Arrow Keys</strong> to adjust position for
                        'Pixel Perfect' placement.
                      </li>
                    </ul>
                  </div>
                </InfoBox>
              </div>
            </div>
          </div>

          <button class="btn btn-primary my-2 mr-2" @click="addSubsectionTag">
            Add Tag
          </button>

          <draggable-elements
            :tags="activeSubsection.tags"
            :property-ids="activeSubsection.propertyIds"
            :subsection-ids="activeSubsection.subsectionIds"
            :background-image="
              activeSubsection.backgroundImage[activeLanguage.code]
            "
          />
        </div>
      </div>
    </card>
  </section>
</template>
<script>
import { includes, find } from "lodash";
import Draggable from "vuedraggable";
import { mapGetters } from "vuex";
import CkEditor from "@/components/CKEditor";
import DotDropper from "@/components/DotDropper";
import InputAsset from "@/components/InputAsset";
import InputKeyValuePairs from "@/components/InputKeyValuePairs";
import InputMultiSelect from "@/components/InputMultiSelect";
import LivingOptionTags from "@/components/LivingOptionTags";
import LivingOptionMarkers from "@/components/LivingOptionMarkers";
import InfoBox from "@/components/InfoBox";
import DraggableElements from "@/components/DraggableElements";
import InputTransitionVideos from "@/components/InputTransitionVideos";

export default {
  components: {
    Draggable,
    CkEditor,
    DotDropper,
    InputAsset,
    InputKeyValuePairs,
    InputMultiSelect,
    LivingOptionTags,
    LivingOptionMarkers,
    InfoBox,
    DraggableElements,
    InputTransitionVideos,
  },

  data() {
    return {
      editingSubsection: null,
      activeSubsectionId: null,
      initialPropertyIds: [],
      subsectionFilterText: "",
      filterActiveStates: [],
      subsectionShortcutSelections: [],
      subsectionShortcutSelectionsFilter: "",
    };
  },

  computed: {
    ...mapGetters(["activeLanguage", "subsections", "properties"]),

    activeSubsection() {
      if (this.editingSubsection) return this.editingSubsection;
      return this.subsections.find(
        (subsection) => subsection._id === this.activeSubsectionId
      );
    },

    subsectionOptions() {
      let subsectionOptions = [];
      this.subsections.forEach((subsection) => {
        const isNotUsedLivingOption = subsection.parent.type !== "livingOption"; // is not being used by a living option
        const isNotUsedSubsection =
          subsection.parent.id === null ||
          subsection.parent.id === this.activeSubsectionId; // is not being used by a different subsection
        const isNotSelf = subsection._id !== this.activeSubsectionId; // is not itself

        if (isNotUsedLivingOption && isNotUsedSubsection && isNotSelf) {
          subsectionOptions.push({
            label: subsection.label,
            value: subsection._id,
          });
        }
      });
      return subsectionOptions;
    },

    subsectionShortcutOptions() {
      let subsectionOptions = [];

      this.subsections.forEach((subsection) => {
        const isNotSelf = subsection._id !== this.activeSubsectionId;

        if (isNotSelf) {
          subsectionOptions.push({
            label: subsection.label,
            value: subsection._id,
          });
        }
      });

      return subsectionOptions.filter((option) =>
        option.label
          .toLowerCase()
          .includes(this.subsectionShortcutSelectionsFilter.toLowerCase())
      );
    },

    propertyOptions() {
      let propertyOptions = [];
      this.properties.forEach((property) => {
        const isNotUsedSubsection =
          property.parent.id === null ||
          property.parent.id === this.activeSubsectionId; // is not being used by a different subsection

        if (isNotUsedSubsection) {
          propertyOptions.push({
            label: property.label,
            value: property._id,
          });
        }
      });
      return propertyOptions;
    },
  },

  watch: {
    activeSubsection(subsection) {
      this.initialPropertyIds = subsection ? subsection.propertyIds : [];

      // ensure field exists
      if (subsection) {
        if (
          !subsection.pageTransitionVideos ||
          typeof subsection.pageTransitionVideos !== "object"
        ) {
          this.$set(subsection, "pageTransitionVideos", {});
        }

        // remove legacy default key if it exists
        if (
          subsection.pageTransitionVideos &&
          "default" in subsection.pageTransitionVideos
        ) {
          const copy = { ...subsection.pageTransitionVideos };
          delete copy.default;
          this.$set(subsection, "pageTransitionVideos", copy);
        }
      }
    },

    subsectionShortcutSelections(ids) {
      const existingIds = this.activeSubsection.subsectionShortcuts.map(
        (shortcut) => shortcut.subsectionId
      );

      let shortcuts = [];

      ids.forEach((id) => {
        if (_.includes(existingIds, id)) {
          const existingShortcut =
            this.activeSubsection.subsectionShortcuts.find(
              (shortcut) => shortcut.subsectionId === id
            );

          shortcuts.push(existingShortcut);
        } else {
          shortcuts.push({
            subsectionId: id,
            label: {
              en: "",
            },
          });
        }
      });

      this.activeSubsection.subsectionShortcuts = shortcuts;
    },
  },

  async created() {
    await this.$store.dispatch("FETCH_ASSETS");
    await this.$store.dispatch("FETCH_SUBSECTIONS");
    if (!this.editingSubsection) {
      this.editingSubsection = this.activeSubsection;
    }
    await this.$store.dispatch("FETCH_PROPERTIES");
    this.initSubsectionShortcutSelections();
  },

  mounted() {
    this.activeSubsectionId = this.$route.params.id;
    this.editingSubsection = this.activeSubsection;
  },

  methods: {
    viewAll() {
      this.$router.push("/live/subsections");
    },

    saveSubsection(subsection) {
      this.$store.dispatch("SAVE_SUBSECTION", subsection);

      // reset parent property on removed properties
      const matchedProperties = this.properties.filter(
        (property) => property.parent.id === this.activeSubsectionId
      );
      const removedPropertiesIds = matchedProperties
        .filter((property) => {
          if (!includes(this.activeSubsection.propertyIds, property._id)) {
            return true;
          }
        })
        .map((property) => property._id);

      let propertyUpdates = [];

      removedPropertiesIds.forEach((propertyId) => {
        const property = find(
          this.properties,
          (property) => propertyId === property._id
        );
        const propertyUpdate = {
          _id: property._id,
          marker: property.marker,
          markers: property.markers,
          polygons: property.polygons,
          parent: {
            id: null,
            type: "",
          },
        };

        // this.$store.dispatch('SAVE_PROPERTY', propertyUpdate)
        propertyUpdates.push(propertyUpdate);
      });

      subsection.propertyIds.forEach((propertyId) => {
        const property = find(
          this.properties,
          (property) => propertyId === property._id
        );
        if (property) {
          const propertyUpdate = {
            _id: property._id,
            marker: property.marker,
            markers: property.markers,
            polygons: property.polygons,
            parent: {
              id: this.activeSubsectionId,
              type: "subsection",
            },
          };
          propertyUpdates.push(propertyUpdate);
        }
      });

      this.$store.dispatch("SAVE_PROPERTIES", propertyUpdates);

      // reset parent property on removed subsections
      const matchedSubsections = this.subsections.filter(
        (subsection) => subsection.parent.id === this.activeSubsectionId
      );
      const removedSubsectionsIds = matchedSubsections
        .filter((subsection) => {
          if (!includes(this.activeSubsection.subsectionIds, subsection._id)) {
            return true;
          }
        })
        .map((subsection) => subsection._id);

      removedSubsectionsIds.forEach((subsectionId) => {
        const subsection = find(
          this.subsections,
          (subsection) => subsectionId === subsection._id
        );
        const subsectionUpdate = {
          _id: subsection._id,
          markers: subsection.markers,
          polygons: subsection.polygons,
          parent: {
            id: null,
            type: "",
          },
        };

        this.$store.dispatch("SAVE_SUBSECTION", subsectionUpdate);
      });

      subsection.subsectionIds.forEach((subsectionId) => {
        const subsection = find(
          this.subsections,
          (subsection) => subsectionId === subsection._id
        );
        const subsectionUpdate = {
          _id: subsection._id,
          markers: subsection.markers,
          polygons: subsection.polygons,
          parent: {
            id: this.activeSubsectionId,
            type: "subsection",
          },
        };

        this.$store.dispatch("SAVE_SUBSECTION", subsectionUpdate);
      });
    },

    addSubsectionTag() {
      this.$store.dispatch("GET_TAG_SCHEMA").then((res) => {
        this.activeSubsection.tags.push(res.data);
      });
    },

    initSubsectionShortcutSelections() {
      if (this.activeSubsection.subsectionShortcuts) {
        this.subsectionShortcutSelections =
          this.activeSubsection.subsectionShortcuts
            .filter((shortcut) => shortcut.subsectionId)
            .map((shortcut) => shortcut.subsectionId);
      }
    },

    getShortcutLabelById(id) {
      const option = this.subsectionShortcutOptions.find(
        (option) => option.value === id
      );

      if (!option) return null;

      return option.label;
    },
  },
};
</script>

<style lang="scss" scoped>
.subsection-shortcuts {
  display: flex;
  flex-direction: column;

  &__inner {
    border-radius: 3px;
    box-shadow: inset 0 0px 5px 0px rgba(96, 125, 159, 0.25);
    overflow: scroll;
    max-height: 280px;
  }
}

.shortcut-wrapper {
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  padding: 0 0.75rem;

  &--darken {
    background: #f3f6fa;
  }

  &--draggable {
    cursor: grab;

    &[draggable="true"] {
      cursor: grabbing;
    }
  }

  label {
    cursor: inherit;
    margin: 0.5rem 0;
    flex-shrink: 0;
  }

  .form-control--sm {
    align-self: center;
    flex-shrink: 1;
    font-size: 0.75rem;
    margin: 0 0 0 0.5rem;
    max-width: 180px;
    padding: 0.1rem 0.3rem;
    width: 100%;
  }

  .shortcut-input-group {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    padding: 0.3rem 0;
    width: 100%;

    label {
      align-self: center;
      font-size: 0.75rem;
      margin: 0;
    }
  }
}

.card-update-tags {
  position: relative;
}

.title-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.info-icon-wrapper {
  display: flex;
  align-items: center;
  transform: translateY(3px);
  z-index: 5000;
}

.edit-tags-info {
  max-width: 380px;

  &__section {
    &:not(:last-child) {
      margin-bottom: 10px;
      padding-bottom: 8px;
      border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    }

    ul {
      margin: 4px 0 0;
      padding-left: 18px;
      font-size: 13px;
      line-height: 1.45;
    }
  }

  &__label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #7a7a85;
    font-weight: 600;
  }
}
</style>
